// src/components/ui/dialogs/Dialog.tsx
'use client';

import React, { useEffect, useCallback, useRef } from 'react';
import { X, Check, AlertCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { cn } from '@/utils/urlUtils';
import { scrollUtils } from '@/utils/scrollUtils';

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children?: React.ReactNode;
  type?: 'success' | 'error' | 'warning' | 'info' | 'custom';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => Promise<void> | void;
  onCancel?: () => void;
  showCancel?: boolean;
  showConfirm?: boolean;
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  disableEscapeClose?: boolean;
  disableOutsideClick?: boolean;
  loading?: boolean;
  customIcon?: React.ReactNode;
  footer?: React.ReactNode;
  preventBodyScroll?: boolean;
  closeOnConfirm?: boolean;
  persistent?: boolean;
}

// Main Dialog component
const Dialog: React.FC<DialogProps> = (props) => {
  // Don't render anything if dialog is not open
  if (!props.isOpen) return null;

  return <DialogContent {...props} />;
};

// Separate content component
const DialogContent: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  type = 'info',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  showCancel = true,
  showConfirm = true,
  showCloseButton = true,
  size = 'md',
  className = '',
  overlayClassName = '',
  contentClassName = '',
  disableEscapeClose = false,
  disableOutsideClick = false,
  loading = false,
  customIcon,
  footer,
  preventBodyScroll = true,
  closeOnConfirm = true,
  persistent = false,
}) => {
  const scrollPositionRef = useRef<number>(0);
  const originalStyleRef = useRef<string>('');

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !disableEscapeClose && !persistent && !loading) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, disableEscapeClose, persistent, loading]);

  // Handle body scroll - IMPROVED VERSION
  useEffect(() => {
    if (isOpen && preventBodyScroll) {
      // Save current scroll position
      scrollPositionRef.current = scrollUtils.saveScrollPosition('dialog-scroll');
      
      // Store original overflow style
      originalStyleRef.current = document.body.style.overflow;
      
      // Prevent scroll
      document.body.style.overflow = 'hidden';
      
      // Add class for CSS fallback
      document.body.classList.add('dialog-open');
      
      return () => {
        // Restore scroll when dialog closes
        document.body.style.overflow = originalStyleRef.current;
        document.body.classList.remove('dialog-open');
        
        // Restore scroll position with a small delay to ensure DOM is ready
        setTimeout(() => {
          scrollUtils.restoreScrollPosition('dialog-scroll', 50);
        }, 50);
      };
    }
  }, [isOpen, preventBodyScroll]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !disableOutsideClick && !persistent && !loading) {
      onClose();
    }
  };

  // Handle confirm
  const handleConfirm = async () => {
    if (onConfirm) {
      try {
        await onConfirm();
        if (closeOnConfirm) {
          onClose();
        }
      } catch (error) {
        console.error('Dialog action failed:', error);
        // Don't close on error
      }
    } else if (closeOnConfirm) {
      onClose();
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  const typeStyles = {
    success: {
      iconBg: 'bg-green-100 dark:bg-green-900/30',
      iconColor: 'text-green-600 dark:text-green-400',
      borderColor: 'border-green-200 dark:border-green-800',
      button: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
      icon: <Check className="w-6 h-6" />,
    },
    error: {
      iconBg: 'bg-red-100 dark:bg-red-900/30',
      iconColor: 'text-red-600 dark:text-red-400',
      borderColor: 'border-red-200 dark:border-red-800',
      button: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
      icon: <X className="w-6 h-6" />,
    },
    warning: {
      iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
      iconColor: 'text-yellow-600 dark:text-yellow-400',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      button: 'bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-500',
      icon: <AlertTriangle className="w-6 h-6" />,
    },
    info: {
      iconBg: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      borderColor: 'border-blue-200 dark:border-blue-800',
      button: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500',
      icon: <Info className="w-6 h-6" />,
    },
    custom: {
      iconBg: 'bg-gray-100 dark:bg-gray-800',
      iconColor: 'text-gray-600 dark:text-gray-400',
      borderColor: 'border-gray-200 dark:border-gray-700',
      button: 'bg-brand-600 hover:bg-brand-700 text-white focus:ring-brand-500',
      icon: null,
    },
  };

  const sizeStyles = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
    full: 'max-w-[95vw] max-h-[95vh]',
  };

  const currentType = typeStyles[type];

  return (
    <>
      {/* Backdrop with higher z-index than header */}
      <div
        className={cn(
          "fixed inset-0 z-[100000] bg-black/50 backdrop-blur-sm transition-opacity",
          overlayClassName
        )}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />
      
      {/* Dialog container */}
      <div className="fixed inset-0 z-[100001] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-auto">
        <div className={cn(
          "relative w-full max-h-[90vh] my-auto",
          sizeStyles[size],
          className
        )}>
          {/* Dialog content */}
          <div
            className={cn(
              "bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden border flex flex-col max-h-[90vh]",
              currentType.borderColor,
              contentClassName
            )}
            style={{ maxHeight: '90vh' }}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  {type !== 'custom' && !customIcon && (
                    <div className={cn(
                      "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
                      currentType.iconBg
                    )}>
                      <div className={currentType.iconColor}>
                        {currentType.icon}
                      </div>
                    </div>
                  )}
                  {customIcon && (
                    <div className="flex-shrink-0">
                      {customIcon}
                    </div>
                  )}
                  {title && (
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {title}
                    </h3>
                  )}
                </div>
                
                {showCloseButton && !loading && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    aria-label="Close"
                    disabled={loading}
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {children}
            </div>

            {/* Footer */}
            {(footer || (showCancel || (showConfirm && onConfirm))) && (
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                {footer ? (
                  footer
                ) : (
                  <div className="flex justify-end gap-3">
                    {showCancel && (
                      <button
                        type="button"
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        {cancelText}
                      </button>
                    )}
                    
                    {showConfirm && onConfirm && (
                      <button
                        type="button"
                        className={cn(
                          "px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
                          currentType.button,
                          loading && "opacity-70 cursor-not-allowed"
                        )}
                        onClick={handleConfirm}
                        disabled={loading}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Processing...
                          </span>
                        ) : (
                          confirmText
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Dialog;